<!-- Loading bar -->
<fuse-loading-bar></fuse-loading-bar>

<!-- Navigation -->
@if (layoutService.isScreenSmall()) {
  <fuse-vertical-navigation
    class="bg-light print:hidden"
    [mode]="'over'"
    [name]="layoutService.isAdminPage() ? 'adminNavigation' : 'mainNavigation'"
    [navigation]="
      layoutService.isAdminPage()
        ? navigationService.filteredNavigation().admin
        : navigationService.filteredNavigation().default
    "
    [opened]="false">
    <!-- Navigation header hook -->
    <ng-container fuseVerticalNavigationContentHeader>
      <!-- Logo -->
      <div class="flex min-h-20 items-center px-8 py-2">
        <app-logo></app-logo>
      </div>
    </ng-container>
  </fuse-vertical-navigation>
}

<!-- Wrapper -->
<div class="flex flex-col flex-auto w-full min-w-0">
  <!-- Banner, header and page. `grow` takes up whatever the wrapper (at least one screen tall,
       html/body are min-h-full flex columns) leaves next to the footer: a short page pushes the
       powered-by footer to the bottom of the screen, a long one puts it right after the content.
       The block itself carries no min-height when the footer is shown — that put the footer
       under the fold on every page (#21752). Only a page that scrolls inside its own container is
       stretched to at least one screen: its `absolute inset-0` region needs that height. Such a
       page claims it with `OwnsScrollDirective` (module templates, and AdminComponent for the
       admin config pages), which puts `dd-owns-scroll` on its host from the first frame, so the
       `has-[.dd-owns-scroll]` class stretches the block before the planning board measures
       itself on init. shrink-0: the block may stretch past the screen, never squeeze under it;
       without it the footer ate 127px out of every page taller than the viewport. -->
  <div
    class="flex flex-col grow shrink-0 basis-auto w-full min-w-0 has-[.dd-owns-scroll]:min-h-[100dvh]"
    data-cy="layout-page-block">
    <app-impersonation-banner></app-impersonation-banner>

    <!-- Header -->
    <!-- Figma "Main nav": 64px bar, 1px bottom border instead of a shadow.
         `sticky top-0` keeps the whole bar on screen while a long page scrolls underneath it, so the
         icons on the right (and the feedback button among them) stay reachable. Sticky, not fixed:
         the bar keeps its place in the flow, so nothing below it needs a top margin to clear it.
         It pins inside this page block, which spans the whole page, and `bg-card` is opaque in both
         schemes, so the page never shows through. It stays a positioned box (what `relative` was
         for): `z-49` and anything absolutely positioned inside it work the same.
         Everything else that pins to the top of the viewport must leave room for it: the shell
         publishes the bar's height as `--dd-sticky-header-offset` (sticky-header-offset.ts, shared
         with the classy shell). The 64px here and that value are one number. -->
    <div
      class="sticky top-0 flex flex-0 items-center w-full h-16 dd-page-gutter z-49 border-b border-[#eeeff1] dark:border-[#2c2c2e] bg-card print:hidden"
      data-cy="layout-header">
      @if (!layoutService.isScreenSmall()) {
        <!-- Logo: fixed size (Figma: 40px tall), never squeezed by nav overflow -->
        <div class="flex shrink-0 items-center mx-2 lg:mr-8">
          <div class="hidden lg:flex shrink-0">
            <app-logo></app-logo>
          </div>
        </div>
        <!-- Horizontal navigation -->
        @if (!layoutService.isAdminPage()) {
          <fuse-horizontal-navigation
            class="w-full mr-2 default-navigation overflow-x-auto"
            data-cy="main-navigation"
            [name]="'mainNavigation'"
            [navigation]="navigationService.filteredNavigation()?.default">
          </fuse-horizontal-navigation>
        }
        @if (layoutService.isAdminPage()) {
          <fuse-horizontal-navigation
            class="mr-2 w-full"
            data-cy="admin-navigation"
            [name]="'adminNavigation'"
            [navigation]="navigationService.navigation()?.admin">
          </fuse-horizontal-navigation>
        }
      }
      <!-- Navigation toggle button -->
      @if (layoutService.isScreenSmall()) {
        <button mat-icon-button (click)="toggleNavigation('mainNavigation')" data-cy="toggle-main-navigation">
          <mat-icon [svgIcon]="'heroicons_outline:bars-3'"></mat-icon>
        </button>
      }

      <!-- icon menu (search, notifications, user) -->
      <!-- dd-drawer-inset-trailing: the nav BAR keeps its full width while an overlay drawer is open
         (the drawer runs over it, per fig frame 509119), but this trailing cluster would then sit
         underneath the panel — including the button that opens the quick-view panel in the first
         place. So the bar stays put and only this cluster steps inward. Rule in _widgets.scss. -->
      <app-icon-menu-items
        ddDrawerInset="trailing"
        class="flex items-center pl-2 ml-auto space-x-0.5 sm:space-x-2"></app-icon-menu-items>
    </div>

    <!-- Content -->
    <!-- dd-drawer-inset-content: reserves room for an open widget overlay drawer so the panel sits
       BESIDE the page instead of on top of it (user decision 2026-08-25). Padding, not width, and
       driven entirely by the `dd-drawer-open` body class + `--dd-drawer-inset` — see _widgets.scss
       for the geometry, including the 1280px floor below which the inset is skipped. -->
    <div ddDrawerInset="content" class="flex flex-col flex-auto w-full">
      <router-outlet></router-outlet>
    </div>
  </div>

  <!-- Footer — hidden wherever the page scrolls inside its own container instead of with the
       document: a footer below that detaches from the inner scroll (it sticks while the content
       scrolls, #21262). `hideFooter` folds both sources of that fact — the admin config slug and
       a module template's own `ddOwnsScroll`. Kept invisible (not removed, so nothing shifts) until the
       page has settled after a navigation: the page block is as tall as its content, so the footer
       would otherwise sit right under the header while the content loads, then jump down
       (`LayoutService.pageSettled`). `motion-reduce` drops the fade, the reveal is then instant. -->
  @if (!layoutService.hideFooter()) {
    <!-- ddDrawerInset="content": the same inset as the page content, so the footer centers on the
         content column while a widget drawer is open instead of on the viewport. The transition
         lists both properties: Tailwind runs with `important: true`, so a plain
         `transition-opacity` would override the inset's 400ms padding glide and make the footer
         jump while the content slides. -->
    <footer
      ddDrawerInset="content"
      class="shrink-0 print:hidden [transition:opacity_200ms,padding-right_400ms_cubic-bezier(0.25,0.8,0.25,1)] motion-reduce:transition-none"
      [class.invisible]="!layoutService.pageSettled()"
      [class.opacity-0]="!layoutService.pageSettled()"
      data-cy="layout-footer">
      <dd-powered-by
        [name]="systemSettingsStore.branding()?.poweredByName ?? null"
        [logoLightSrc]="systemSettingsStore.branding()?.light?.poweredByLogo?.blobUrl ?? null"
        [logoDarkSrc]="systemSettingsStore.branding()?.dark?.poweredByLogo?.blobUrl ?? null"
        [url]="systemSettingsStore.branding()?.poweredByUrl ?? null"
        [logoHeight]="poweredByLogoHeight()"></dd-powered-by>
    </footer>
  }
</div>
